// src/marketing/docs/markdown.ts
// Renderer Markdown maison — zéro dépendance.
// Sous-ensemble : h1-h6, **gras**, *italique*, `code inline`, ```blocs```,
// > citations, listes - / * / 1., [lien](url), <hr> (---), tableaux, paragraphes.
// L'échappement HTML est appliqué EN PREMIER sur l'entrée brute (anti-XSS).

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// Applique les règles inline (gras, italique, code, liens) sur du texte DÉJÀ ÉCHAPPÉ
function applyInline(text: string): string {
  // Code inline : protéger avant les autres transformations
  // On remplace temporairement avec un marqueur, puis on restaure
  const inlineCodes: string[] = []
  text = text.replace(/`([^`]+)`/g, (_match, inner) => {
    // inner est déjà échappé (applyInline reçoit toujours du texte pré-échappé)
    inlineCodes.push(inner as string)
    return `\x01ICODE${inlineCodes.length - 1}\x01`
  })

  // Gras
  text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  // Italique
  text = text.replace(/\*([^*]+)\*/g, '<em>$1</em>')
  // Liens — bloquer les URLs dangereuses (javascript:, data:, vbscript:)
  // Rejeter aussi les URLs contenant des espaces (URLs malformées)
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, label, url) => {
    const cleanUrl = (url as string).trim()
    if (/^(javascript|data|vbscript):/i.test(cleanUrl)) {
      return label as string
    }
    if (/\s/.test(cleanUrl)) {
      // URL malformée (contient des espaces) — rendre le label en texte brut
      return label as string
    }
    return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer">${label}</a>`
  })

  // Restaurer les codes inline
  text = text.replace(/\x01ICODE(\d+)\x01/g, (_match, idx) => {
    const code = inlineCodes[parseInt(idx as string, 10)] ?? ''
    return `<code>${code}</code>`
  })

  return text
}

function renderTable(block: string): string {
  const lines = block.trim().split('\n')
  if (lines.length < 2) return `<p>${applyInline(block)}</p>`
  const headerLine = lines[0] ?? ''
  // lines[1] est la ligne de séparation |---|---| — ignorée
  const bodyLines = lines.slice(2)

  const parseRow = (line: string) =>
    line
      .trim()
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((cell) => cell.trim())

  // Les cellules sont déjà échappées (proviennent de `processed`) —
  // NE PAS rappeler escapeHtml() ici pour éviter le double-échappement.
  const headers = parseRow(headerLine)
  const headerHtml = headers.map((h) => `<th>${applyInline(h)}</th>`).join('')
  const bodyHtml = bodyLines
    .map((line) => {
      const cells = parseRow(line)
      return `<tr>${cells.map((c) => `<td>${applyInline(c)}</td>`).join('')}</tr>`
    })
    .join('')

  return `<table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`
}

export function renderMarkdown(input: string): string {
  // 1. Protéger les blocs de code avant tout traitement (marqueur \x00CODEn\x00)
  const codeBlocks: string[] = []
  let processed = input.replace(/```[\s\S]*?```/g, (match) => {
    // Retirer le marqueur de langage optionnel sur la première ligne
    const inner = match.slice(3, -3).replace(/^[^\n]*\n?/, '')
    codeBlocks.push(escapeHtml(inner))
    return `\x00CODE${codeBlocks.length - 1}\x00`
  })

  // 2. Échapper le HTML sur le reste (hors blocs de code déjà traités)
  processed = processed.replace(/\x00CODE\d+\x00|[&<>"']/g, (m) => {
    if (m.startsWith('\x00')) return m
    return escapeHtml(m)
  })

  // Note : pas de passe /\bon\w+=/gi ici — après l'échappement HTML,
  // aucun attribut réel ne peut se former (<img onerror=...> → &lt;img onerror=...&gt;
  // est déjà inerte). Cette passe corromprait le texte légitime (ex: "once=5").

  const lines = processed.split('\n')
  const output: string[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i] ?? ''

    // Restauration des blocs de code
    const codeMatch = line.match(/^\x00CODE(\d+)\x00$/)
    if (codeMatch && codeMatch[1] !== undefined) {
      const code = codeBlocks[parseInt(codeMatch[1], 10)] ?? ''
      output.push(`<pre><code>${code}</code></pre>`)
      i++
      continue
    }

    // Titres
    const hMatch = line.match(/^(#{1,6})\s+(.+)/)
    if (hMatch && hMatch[1] && hMatch[2]) {
      const level = hMatch[1].length
      output.push(`<h${level}>${applyInline(hMatch[2])}</h${level}>`)
      i++
      continue
    }

    // Séparateur ---
    if (/^---+$/.test(line.trim())) {
      output.push('<hr />')
      i++
      continue
    }

    // Citation (> déjà transformé en &gt; par l'échappement HTML — détecter &gt;)
    if (line.startsWith('&gt; ')) {
      const text = line.slice(5)
      output.push(`<blockquote>${applyInline(text)}</blockquote>`)
      i++
      continue
    }

    // Tableau (| déjà présent car | n'est pas échappé)
    if (line.startsWith('|')) {
      const tableLines: string[] = [line]
      i++
      while (i < lines.length && (lines[i] ?? '').startsWith('|')) {
        tableLines.push(lines[i] ?? '')
        i++
      }
      output.push(renderTable(tableLines.join('\n')))
      continue
    }

    // Liste non ordonnée (- et * ne sont pas échappés)
    if (/^[-*]\s/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^[-*]\s/.test(lines[i] ?? '')) {
        const rawLine = lines[i] ?? ''
        // Cases à cocher GitHub-style : - [ ] ou - [x] / - [X]
        // L'entrée est déjà HTML-échappée à ce stade — les crochets [ ] ne sont pas
        // affectés par escapeHtml, donc la regex s'applique directement sans double-escape.
        const taskMatch = rawLine.match(/^- \[([ xX])\] (.*)$/)
        if (taskMatch) {
          const checked = taskMatch[1] !== ' '
          const content = taskMatch[2] ?? ''
          const cbClass = checked ? 'cb done' : 'cb'
          items.push(
            `<li class="task"><span class="${cbClass}"></span>${applyInline(content)}</li>`,
          )
        } else {
          items.push(`<li>${applyInline(rawLine.replace(/^[-*]\s/, ''))}</li>`)
        }
        i++
      }
      output.push(`<ul>${items.join('')}</ul>`)
      continue
    }

    // Liste ordonnée
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\d+\.\s/.test(lines[i] ?? '')) {
        items.push(`<li>${applyInline((lines[i] ?? '').replace(/^\d+\.\s/, ''))}</li>`)
        i++
      }
      output.push(`<ol>${items.join('')}</ol>`)
      continue
    }

    // Ligne vide
    if (line.trim() === '') {
      i++
      continue
    }

    // Paragraphe — agrège les lignes suivantes non-structurées
    const paraLines: string[] = [line]
    i++
    while (
      i < lines.length &&
      (lines[i] ?? '').trim() !== '' &&
      !/^[#|]/.test(lines[i] ?? '') &&
      !/^[-*]\s/.test(lines[i] ?? '') &&
      !/^\d+\.\s/.test(lines[i] ?? '') &&
      !/^---+$/.test((lines[i] ?? '').trim()) &&
      !/^&gt;\s/.test(lines[i] ?? '') &&
      !(lines[i] ?? '').match(/^\x00CODE\d+\x00$/)
    ) {
      paraLines.push(lines[i] ?? '')
      i++
    }
    output.push(`<p>${applyInline(paraLines.join(' '))}</p>`)
  }

  return output.join('\n')
}
